import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import LaunchIcon from '@mui/icons-material/Launch'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import { validateField, validateUrlField, validateOptionalField, descriptionLimit, validateRequiredResourceTags, collectTagData, validateSubResourceRows } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { setErrorNotification } from '../../reducers/notificationReducer'
import { openExternal } from '../../utils/safeUrl'
import useSubResourceRows from '../../hooks/useSubResourceRows'

// Shared admin edit modal for the media types whose resources carry nested
// sub-resource rows (Course lessons, Podcast talks, Video playlist entries).
// Everything that differs per type arrives through `config`; see the thin
// wrappers AdminCourseModal / AdminPodcastModal / AdminVideoModal.
const AdminMediaModal = ({ config, ...props }) => {
    const { Icon, style, subKey, idPrefix, formLabel, rowsNoun, rowsHeading, rowLabel, update, remove } = config
    const subRowsProp = props[subKey]
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    // form state (no need to use redux for form state)
    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])
    const { rows, setRows, handleFormChange, addFields, removeFields } = useSubResourceRows()

    // init fields
    useEffect(() => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
        if (subRowsProp && subRowsProp.length > 0) setRows(subRowsProp.map(row => { return {...row}}))
    }, [props.title, props.authors, props.link, props.pub_date, props.description, subRowsProp, props.content_tags, props.medical_specialty_tags, props.admin_category_tags, props.experience_tags, props.demographic_tags, props.tags, props.is_seminal_work, props.in_quiz, props.is_tcairem, props.is_paid, props.tcairem_type_tags, props.tcairem_persona_tags, setRows])

    // reset fields to original values if user wants to
    const reset = () => {
        setName(props.title)
        setAuthors(props.authors)
        setLink(props.link)
        setDate(props.pub_date)
        setDescription(props.description || '')
        setContentTags((props.content_tags || props.tags || []).map(String))
        setMedicalTags((props.medical_specialty_tags || []).map(String))
        setAdminCategoryTags((props.admin_category_tags || []).map(String))
        setExperienceTags((props.experience_tags || []).map(String))
        setDemographicTags((props.demographic_tags || []).map(String))
        setIsSeminalWork(props.is_seminal_work || false)
        setInQuiz(props.in_quiz || false)
        setIsTcairem(props.is_tcairem || false)
        setIsPaid(props.is_paid || false)
        setTcairemTags((props.tcairem_type_tags || []).map(String))
        setPersonaTags((props.tcairem_persona_tags || []).map(String))
        if (subRowsProp && subRowsProp.length > 0) setRows(subRowsProp.map(row => { return {...row}}))
        else setRows([])

        setNameError(false)
        setNameHelper('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLinkError(false)
        setLinkHelper('')
        setDateError(false)
        setDateHelper('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setShowTagErrors(false)
    }

    // delete resource
    const handleReject = async () => {
        if (window.confirm('Are you sure? Note this resource cannot be retrieved after rejection.')) {
            handleClose()
            await dispatch(remove(props.id, user.staff))
            props.onChanged?.()
        }
    }

    // "Save Changes" is the form's ONLY type='submit' button, so this handler
    // receives both an explicit click on it and implicit submission (Enter in a
    // field). Neither may touch approval state -- that was issue #390.
    const handleSubmit = (event) => {
        event.preventDefault()
        handleSave()
    }

    // Approve/Disapprove: a type='button' with its own entry point, so no form
    // submission is involved. Toggles approved state and saves current values with it.
    const handleApprove = async () => {
        setShowTagErrors(true)

        const fieldsValid = (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            validateUrlField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        )
        // This path no longer triggers native form validation, so the nested
        // rows' `required` markup must be checked here (issue #390). Evaluated
        // after the parent chain so the setXError side effects above still run
        // in their original order.
        const rowsValid = validateSubResourceRows(rows)

        if (fieldsValid && rowsValid) {
            if (window.confirm(`${props.approved ? 'Disapprove' : 'Approve'} this resource? Any changes you have made will be included.`)) {
                const data = {
                    ...props,
                    title: name,
                    description: description,
                    authors: authors,
                    link: link,
                    pub_date: date,
                    approved: !props.approved,
                    is_seminal_work: isSeminalWork,
                    in_quiz: inQuiz,
                    is_tcairem: isTcairem,
                    is_paid: isPaid,
                    ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
                }

                handleClose()
                await dispatch(update(props.id, data, rows, user.staff))
                props.onChanged?.()
            }
        } else if (fieldsValid) {
            dispatch(setErrorNotification(`Please fill all required fields in the ${rowsNoun} before submission`))
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    // do not change approved state just update fields
    const handleSave = async () => {
        setShowTagErrors(true)
        const fieldsValid = (
            validateField(name, setNameError, setNameHelper) &&
            validateField(authors, setAuthorsError, setAuthorsHelper) &&
            // On the Save path a real browser blocks submission at the native
            // type='url' check before this runs, so this call is defence-in-depth
            // rather than the operative guard. The Approve path has no native
            // check, which is why the helper exists (issue #390).
            validateUrlField(link, setLinkError, setLinkHelper) &&
            validateField(date, setDateError, setDateHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, descriptionLimit(props.description), 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        )
        // Evaluated after the parent chain so the setXError side effects above
        // still run in their original order.
        const rowsValid = validateSubResourceRows(rows)

        if (fieldsValid && rowsValid) {
            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            const ok = await dispatch(update(props.id, data, rows, user.staff))
            if (ok) {
                handleClose()
                props.onChanged?.()
            }
        } else if (fieldsValid) {
            dispatch(setErrorNotification(`Please fill all required fields in the ${rowsNoun} before submission`))
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const [open, setOpen] = useState(false)

    const handleClickOpen = () => {
        setShowTagErrors(false)
        setOpen(true)
    }

    const handleClose = () => {
        setShowTagErrors(false)
        setOpen(false)
    }

    const openResource = () => {
        openExternal(props.link)
        return false;
    }

    return (
        <div>
            <Button {...style.viewButton.props} onClick={()=>handleClickOpen()} sx={style.viewButton.style}>
                <EditIcon /> &nbsp; Edit
            </Button>
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`${idPrefix}-modal-${props.id}`}
                open={open}
                fullWidth
                {...style.modal}
            >
                <BootstrapDialogTitle id={`${idPrefix}-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={style.titleContainer}>
                        <Icon sx={style.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers sx={style.content}>
                    <form onSubmit={handleSubmit} id={`${idPrefix}-form-${props.id}`} aria-label={formLabel}>
                        <Typography {...style.subheading} gutterBottom>
                            Manage Resource
                        </Typography>
                        {props.proposer && <Typography {...style.proposerText}>
                            Created By: {props.proposer.name} ({props.proposer.email})
                        </Typography>}
                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                            <CoreResourceFields
                                name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                                authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                                link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                                date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                                validate={validateField}
                                linkValidate={validateUrlField}
                            />
                            <ResourceDescriptionField
                                value={description} setValue={setDescription}
                                error={descriptionError} setError={setDescriptionError}
                                helper={descriptionHelper} setHelper={setDescriptionHelper}
                                limit={descriptionLimit(props.description)}
                            />
                            <ResourceTagsSection
                                contentTags={contentTags} setContentTags={setContentTags}
                                medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                                adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                                experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                                required={true}
                                forceShowRequiredErrors={showTagErrors}
                            />
                            <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                            <TCAIREMFieldsSection
                                isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                                isPaid={isPaid} setIsPaid={setIsPaid}
                                tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                                personaTags={personaTags} setPersonaTags={setPersonaTags}
                                forceShowRequiredErrors={showTagErrors}
                            />
                        </Grid>
                        <Grid container sx ={style.subFormHeading} spacing={2}>
                            <Grid item xs={7} sm={9}>
                                <Typography {...style.subFormHeadingText} gutterBottom>
                                    {rowsHeading}
                                </Typography>
                            </Grid>
                            <Grid item xs={5} sm={3}>
                                <Button {...style.add.props} sx={style.add.style} onClick={()=>addFields()}>
                                    Add {rowLabel}&nbsp;&nbsp;&nbsp;<AddCircleIcon />
                                </Button>
                            </Grid>
                        </Grid>
                        {rows.map((row, index) => (
                            <Grid item key={index} xs={12} sx={style.subFormGrid}>
                                <Grid container spacing={2}>
                                    <Grid item xs={7} sm={9}>
                                        <Typography {...style.subFormHeadingText} gutterBottom>
                                            {rowLabel} {index+1}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5} sm={3}>
                                        <Button sx={style.deleteButton.style} {...style.deleteButton.props} onClick={()=>removeFields(index)}>
                                            Remove {rowLabel}&nbsp;&nbsp;&nbsp;<DeleteIcon />
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            name='title'
                                            required
                                            value={row.title}
                                            onChange={(event)=>handleFormChange(index, event)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Authors"
                                            name='authors'
                                            required
                                            value={row.authors}
                                            onChange={(event)=>handleFormChange(index, event)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Link"
                                            name='link'
                                            required
                                            type='url'
                                            value={row.link}
                                            onChange={(event)=>handleFormChange(index, event)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Publish Date"
                                            name='pub_date'
                                            required
                                            type='date'
                                            InputLabelProps={{ shrink: true }}
                                            value={row.pub_date}
                                            onChange={(event)=>handleFormChange(index, event)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                        <Button {...style.resetButton} type='button' onClick={() => reset()} sx={{marginTop: '20px'}}>
                            Reset Fields
                        </Button>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button type='button' onClick={()=>openResource()}>
                        View Resource&nbsp;<LaunchIcon {...style.launch}/>
                    </Button>
                    <Button type='submit' form={`${idPrefix}-form-${props.id}`}>
                        Save Changes
                    </Button>
                    <Button type='button' onClick={()=>handleApprove()}>
                        {props.approved? 'Disapprove':'Approve'}
                    </Button>
                    <Button type='button' onClick={()=>handleReject()}>
                        {props.approved? 'Delete':'Reject'}
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default AdminMediaModal
